import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import configData from "../../config/config.json";
import { useDispatch, useSelector } from "react-redux";
import { Form, Container, Row, Button, Col } from "react-bootstrap";

import PageBanner from "../common/page/PageBanner.js";
import PrimaryButton from "../common/form/PrimaryButton.js";
import TextField from "../common/form/TextField.js";
import SelectField from "../common/form/SelectField.js";

import Alert from "../common/modal/Alert.js";
import AlertEntryEmailRow from "./AlertEntryEmailRow.js";
import AlertEntryPhoneRow from "./AlertEntryPhoneRow.js";

import Loading from "../common/modal/Loading.js";
import Confirmation from "../common/modal/Confirmation.js";

import EmailAddressSelectionList from "../common/selection/EmailAddressSelectionList.js";
import PhoneNumberSelectionList from "../common/selection/PhoneNumberSelectionList.js";

import { setSelectedControlPanelType, HealthCheckListControlPanelKey } from "../../redux/actions/controlPanelActions.js";
import { saveHealthCheck } from "../../redux/actions/alertActions.js";

import { formatPhone } from "../common/function/formattingUtils.js";
import { ALERT_TYPES, LOADING_STATUES } from "../constants/constants.js";
import { trimAlertFields, validateHealthCheck } from "../common/function/alertValidation.js";

const componentName = HealthCheckListControlPanelKey;

export default function HealthCheckEntry(props) {
	const dispatch = useDispatch();
	const alertSettings = useSelector((state) => state.countyConfigAlertSettingsReducer);
	const sideNavigationOpen = useSelector((state) => state.sideNavigationReducer);
	const alertListFilters = useSelector((state) => state.alertListFiltersReducer);
	const multiTenantSettings = useSelector((state) => state.countyConfigMultiTenantSettingsReducer);

	// ---------------- STATE ----------------
	const [state, setState] = useState({
		id: 0,
		countyList: [{ 'NONE': '' }],
		countyCode: null,
		dataType: "MOCK",
		criteriaFormatType: "COMBINED",
		commaAfterLastName: false,
		alertType: "BUSINESS",
		frequencyType: "MONTHLY",
		name: "",
		firstName: "",
		lastName: "",
		businessName: "",
		mockBusinessName: "",
		mockFirstName: "",
		mockLastName: "",
		mockName: "",
		healthCheckEmailAddresses: [],
		healthCheckPhones: [],
		parcelId: "",
		selectedEmailField: "",
		selectedPhoneField: "",
		statusType: "A",
		showDeleteConfirmationDialog: false,
		loadingStatus: "",
		alertComponentType: ALERT_TYPES.OK,
		errors: {},
		showAlert: false,
		alertMessage: "",
		redirect: false,
		showEmailAddressList: false,
		showPhoneNumberList: false
	});

	const updateState = (patch) =>
		setState((prev) => ({ ...prev, ...patch }));

	// ---------------- INIT ----------------
	useEffect(() => {
		dispatch(setSelectedControlPanelType(componentName));

		axios.get(configData.LANDNOTIFICATION_SERVER_URL + "helperdata/multiTenantList", { withCredentials: true })
			.then((res) => {
				updateState({ countyList: res.data.content.codeDescriptionResultList });
			})
			.catch(console.error);

		const id = props.match.params.id;

		if (id === "0") {
			addEmail();
			addPhone();
			return;
		}

		axios.get(configData.LANDNOTIFICATION_SERVER_URL + "controlPanel/healthCheck/" + id, { withCredentials: true })
			.then((res) => {
				updateState(mapContentToState(res.data.content));
			})
			.catch(console.error);
	}, []);

	useEffect(() => {
		if (state.redirect === true) {
			props.history.push('/controlPanel/healthChecks');
		}
	}, [state.redirect, props.history]);

	const mapContentToState = (content) => ({
		id: content.id,
		userId: content.userId,
		username: content.username,
		countyCode: content.countyCode,
		alertType: content.alertType,
		dataType: content.dataType,
		frequencyType: content.frequencyType,
		criteriaFormatType: content.criteriaFormatType,
		commaAfterLastName: content.commaAfterLastName,
		name: content.name,
		firstName: content.firstName,
		lastName: content.lastName,
		businessName: content.businessName,
		parcelId: content.parcelId,
		mockBusinessName: content.mockBusinessName,
		mockFirstName: content.mockFirstName,
		mockLastName: content.mockLastName,
		mockName: content.mockName,
		mockParcelId: content.mockParcelId,
		healthCheckEmailAddresses: content.healthCheckEmailAddresses,
		healthCheckPhones: content.healthCheckPhones,
		statusType: content.statusType,
	});

	// ---------------- LOADING ----------------
	const showLoading = useCallback(() => {
		if (state.loadingStatus === "") {
			updateState({ loadingStatus: LOADING_STATUES.LOADING });
			return true;
		}
		return false;
	}, [state.loadingStatus]);

	const hideLoading = () => updateState({ loadingStatus: "" });

	// ---------------- INPUT CHANGE ----------------
	const handleChange = (event) => {
		const { id, value, type } = event.target;
	
		if (id.includes("emailEntry")) {
			const index = event.currentTarget.id.slice(-1);
			const list = [...state.healthCheckEmailAddresses];
			list[index] = { ...list[index], value, saved: false };
			return updateState({ healthCheckEmailAddresses: list, errors: {} });
		}

		if (id.includes("phoneEntry")) {
			const index = event.currentTarget.id.slice(-1);
			const list = [...state.healthCheckPhones];
			const formatted = formatPhone(value, list[index].value);
			list[index] = { ...list[index], value: formatted, saved: false };
			return updateState({ healthCheckPhones: list, errors: {} });
		}

		if (id.includes('emailAcknowledge')) {
			const index = event.currentTarget.id.slice(-1);
			const list = [...state.healthCheckEmailAddresses];
			let acknowledged = list[index].acknowledged === true ? false : true;
			list[index] = { ...list[index], acknowledged, saved: false };
			return updateState({ healthCheckEmailAddresses: list, errors: {} });
		}

		if (id.includes('phoneAcknowledge')) {
			const index = event.currentTarget.id.slice(-1);
			const list = [...state.healthCheckPhones];
			let acknowledged = list[index].acknowledged === true ? false : true;
			list[index] = { ...list[index], acknowledged, saved: false };
			return updateState({ healthCheckPhones: list, errors: {} });
		}

		updateState({
			[id]: value || (type === "number" ? 0 : value),
			errors: {},
		});
	};

	const handleEmailLookup = (event) => {
		event.preventDefault();

		const fieldNumber = event.currentTarget.id.slice(-1);
		if (!state.userId) {
			let errors = {};
			errors["userLookupButton"] = "Required.";
			errors["email" + fieldNumber] = "First select a User.";
			updateState({
				errors
			})
			return;
		}

		updateState({
			selectedEmailField: fieldNumber,
			showEmailAddressList: true
		});
	}

	const handleCloseEmailLookup = (selectedId, selectedName) => {
		let index = state.selectedEmailField;
		index = parseInt(index);
		let healthCheckEmailAddresses = state.healthCheckEmailAddresses;
		healthCheckEmailAddresses[index].id = selectedId;
		healthCheckEmailAddresses[index].value = selectedName;
		updateState({
			healthCheckEmailAddresses,
			showEmailAddressList: false,
		})
	}

	const handlePhoneLookup = (event) => {
		event.preventDefault();

		const fieldNumber = event.currentTarget.id.slice(-1);
		if (!state.userId) {
			let errors = {};
			errors["userLookupButton"] = "Required.";
			errors["phone" + fieldNumber] = "First select a User.";
			updateState({
				errors
			})
			return;
		}

		updateState({
			selectedPhoneField: fieldNumber,
			showPhoneNumberList: true
		});
	}

	const handleClosePhoneLookup = (selectedId, selectedName) => {
		let index = state.selectedPhoneField;
		index = parseInt(index);
		let healthCheckPhones = state.healthCheckPhones;
		healthCheckPhones[index].id = selectedId;
		healthCheckPhones[index].value = selectedName;
		updateState({
			healthCheckPhones,
			showPhoneNumberList: false,
		})
	}

	// ---------------- EMAIL / PHONE ----------------
	const addEmail = () => {
		if (state.healthCheckEmailAddresses.length >= alertSettings.numberOfEmails) {
			return;
		}

		updateState({
			healthCheckEmailAddresses: [
				...state.healthCheckEmailAddresses,
				{ id: 0, value: "", verified: false, acknowledged: false, saved: false },
			],
		});
	};

	const addPhone = () => {
		if (state.healthCheckPhones.length >= alertSettings.numberOfPhones) {
			return;
		}

		updateState({
			healthCheckPhones: [
				...state.healthCheckPhones,
				{ id: 0, value: "", verified: false, acknowledged: false, saved: false },
			],
		});
	};

	const deleteEmail = (e) => {
		const index = e.currentTarget.id.slice(-1);
		const list = [...state.healthCheckEmailAddresses];
		list.splice(index, 1);
		updateState({ healthCheckEmailAddresses: list });
	};

	const deletePhone = (e) => {
		const index = e.currentTarget.id.slice(-1);
		const list = [...state.healthCheckPhones];
		list.splice(index, 1);
		updateState({ healthCheckPhones: list });
	};

	const resendEmailVerification = (event) => {
		event.preventDefault();
		const index = event.target.id.slice(-1);
		let id = state.healthCheckEmailAddresses[index].id;
		if (showLoading() === true) {
			let request = {
				id
			};

			axios.post(configData.LANDNOTIFICATION_SERVER_URL + "userView/alertResendEmail", request, { withCredentials: true })
				.then((response) => {
					updateState({ loadingStatus: "", redirect: false, showAlert: true, alertMessage: response.data.message });
				}, (error) => {
					updateState({ loadingStatus: "", redirect: false, showAlert: true, alertMessage: error.response.data.message});
				});
		}
	}

	const resendPhoneVerification = (event) => {
		event.preventDefault();
		const index = event.target.id.slice(-1);
		let id = state.healthCheckPhones[index].id;
		if (showLoading() === true) {
			let request = {
				id
			};

			axios.post(configData.LANDNOTIFICATION_SERVER_URL + "userView/alertResendPhone", request, { withCredentials: true })
				.then((response) => {
					updateState({ loadingStatus: "", redirect: false, showAlert: true, alertMessage: response.data.message });
				}, (error) => {
					updateState({ loadingStatus: "", redirect: false, showAlert: true, alertMessage: error.response.data.message});
				});
		}
	}

	// ---------------- SUBMIT ----------------
	const handleSubmit = (event) => {
		event.preventDefault();
		const trimmedAlertFields = trimAlertFields(state);
		updateState({ ...trimmedAlertFields });
		validateAndSubmit();
	}

	const validateAndSubmit = () => {
		let errors = validateHealthCheck(state, state.id === 0, multiTenantSettings.multiTenant === true);
		if (errors && Object.keys(errors).length > 0) {
			updateState({ errors });
			return;
		}
		if (showLoading() === true) {
			let { alertComponentType, username, selectedEmailField, selectedPhoneField, showDeleteConfirmationDialog, loadingStatus, errors, showAlert, alertMessage, redirect, showUserList, showEmailAddressList, showPhoneNumberList, ...request } = state; // remove UI only fields before submitting
			dispatch(saveHealthCheck(request, handleSaveResult));
		}
	}

	const handleSaveResult = (redirect, errorFound, message) => {
		updateState({
			loadingStatus: "",
			redirect,
			alertComponentType: errorFound ? "WARNING" : "",
			showAlert: true,
			alertMessage: message,
		});
	};

	// ---------------- DELETE ----------------
	const handleDeleteAlert = () => {
		if (!showLoading()) return;
		let request = {
			alertId: state.id,
			filterText: alertListFilters.filterText,
			includeInactive: alertListFilters.includeInactive
		};
		axios.post(configData.LANDNOTIFICATION_SERVER_URL + "controlPanel/healthCheckDelete", request, { withCredentials: true })
			.then((res) => {
				updateState({ loadingStatus: "", redirect: true, showAlert: true, alertMessage: res.data.message });
			})
			.catch((error) => {
				updateState({ loadingStatus: "", redirect: false, showAlert: true, alertMessage: error.response.data.message});
			});
	};

	const showDeleteConfirmation = (event) => {
		event.preventDefault();
		updateState({
			showDeleteConfirmationDialog: true
		});
	}

	const hideDeleteConfirmation = () => {
		updateState({
			showDeleteConfirmationDialog: false
		});
	}

	const disabledFlag = state.id !== 0;

	let county = <></>;
	if (multiTenantSettings.multiTenant === true) {
		county = <SelectField formGroup={{ id: 'countyCode', label: 'County', fieldType: 'select', primaryCriteria: true, value: state.countyCode, required: true, options: state.countyList }} disabled={disabledFlag} onChange={handleChange} />
	}

	let alertTypeOptions = [];
	if (alertSettings.businessAlert) {
		alertTypeOptions.push({ code: "BUSINESS", description: "Business/Trust" });
	}
	if (alertSettings.nameAlert) {
		alertTypeOptions.push({ code: "NAME", description: "Person" });
	}
	if (alertSettings.parcelIdAlert) {
		alertTypeOptions.push({ code: "PARCEL_ID", description: "Parcel ID" });
	}
	let alertType = <SelectField formGroup={{ id: 'alertType', label: 'Monitor', fieldType: 'select', primaryCriteria: true, value: state.alertType, required: true, options: alertTypeOptions }} disabled={disabledFlag} onChange={handleChange} />

	if (disabledFlag) {
		let alertTypeDescription = "Parcel ID";
		if (state.alertType === 'BUSINESS') {
			alertTypeDescription = "Business/Trust";
		} else if (state.alertType === 'NAME') {
			alertTypeDescription = "Person";
		}
		alertType = <TextField formGroup={{ id: 'alertType', label: 'Monitor', fieldType: 'text', value: alertTypeDescription, primaryCriteria: true, disableField: true }} />
	}
	let realDataSection = <></>;
	if (state.dataType === 'REAL') {
		realDataSection = <div style={{ marginLeft: 50 }}>
			<Form.Row>
				<Form.Group controlId='criteriaFormatType'>
					<SelectField formGroup={{ id: 'criteriaFormatType', label: 'Name Format', fieldType: 'select', primaryCriteria: true, value: state.criteriaFormatType, required: true, options: [{ code: "COMBINED", description: "Combined Name" }, { code: "SPLIT", description: "Split Name" }] }} disabled={disabledFlag} onChange={handleChange} />
					<div style={{ color: "red", fontWeight: "bold" }}>{state.errors['criteriaFormatType']}</div>
				</Form.Group>
			</Form.Row>
			{
				state.criteriaFormatType === 'COMBINED' &&
				<Form.Row>
					<Form.Group controlId='commaAfterLastName'>
						<SelectField formGroup={{ id: 'commaAfterLastName', label: 'Comma After Last Name', fieldType: 'select', primaryCriteria: true, value: state.commaAfterLastName, required: true }} disabled={disabledFlag} onChange={handleChange} />
						<div style={{ color: "red", fontWeight: "bold" }}>{state.errors['commaAfterLastName']}</div>
					</Form.Group>
				</Form.Row>
			}
			</div>;	
	}
	let mockDataSection = <></>;
	if (state.dataType === 'MOCK') {
		mockDataSection =
			<div style={{ marginLeft: 50 }}>
				<h6 style={{ textDecoration: 'underline' }}>Alert Criteria - use a criteria as bogus as possible to avoid unintentional alerts to real users</h6>
				<Form.Row>
					<Form.Group>{alertType}</Form.Group>
				</Form.Row>
				{
					state.alertType === "NAME" &&
					<Form.Row>
						<Form.Group controlId='firstName'>
							<TextField formGroup={{ id: "firstName", label: "First Name", value: state.firstName, required: true, primaryCriteria: true  }} onChange={handleChange} />
						</Form.Group>
						<Form.Group controlId='lastName'>
							<TextField formGroup={{ id: "lastName", label: "Last Name", value: state.lastName, required: true, primaryCriteria: true  }} onChange={handleChange} />
						</Form.Group>
					</Form.Row>

				}
				{
					state.alertType === "BUSINESS" &&
					<Form.Row>
						<Form.Group controlId='businessName'>
							<TextField formGroup={{ id: "businessName", label: "Business Name", value: state.businessName, required: true, primaryCriteria: true  }} onChange={handleChange} />
						</Form.Group>
					</Form.Row>
				}
				{
					state.alertType === "PARCEL_ID" &&
					<Form.Row>
						<Form.Group controlId='parcelId'>
							<TextField formGroup={{ id: "parcelId", label: "Parcel ID", value: state.parcelId, required: true, primaryCriteria: true  }} onChange={handleChange} />
						</Form.Group>
					</Form.Row>

				}
				<h6 style={{ textDecoration: 'underline' }}>Mock Data - same format as how you normally index</h6>
				{
					state.alertType === 'NAME' &&
					<Form.Row>
						<Form.Group controlId='criteriaFormatType'>
							<SelectField formGroup={{ id: 'criteriaFormatType', label: 'Name Format', fieldType: 'select', primaryCriteria: true, value: state.criteriaFormatType, required: true, options: [{ code: "COMBINED", description: "Combined Name" }, { code: "SPLIT", description: "Split Name" }] }} disabled={disabledFlag} onChange={handleChange} />
							<div style={{ color: "red", fontWeight: "bold" }}>{state.errors['criteriaFormatType']}</div>
						</Form.Group>
					</Form.Row>
				}
				<Form.Row>
					{
						(state.alertType === 'NAME' && state.criteriaFormatType === 'SPLIT') &&
						<Form.Group controlId='mockFirstName'>
							<TextField formGroup={{ label: 'First Name', fieldType: 'text', value: state.mockFirstName, required: true, primaryCriteria: false }} onChange={handleChange} />
							<div style={{ fontSize: '0.75em', marginLeft: 3 }} >Same format as how you index a name.</div>
							<div style={{ color: "red", fontWeight: "bold" }}>{state.errors['mockFirstName']}</div>
						</Form.Group>
					}
					{
						(state.alertType === 'NAME' && state.criteriaFormatType === 'SPLIT') &&
						<Form.Group controlId='mockLastName'>
							<TextField formGroup={{ label: 'LAST Name', fieldType: 'text', value: state.mockLastName, required: true, primaryCriteria: true }} onChange={handleChange} />
							<div style={{ color: "red", fontWeight: "bold" }}>{state.errors['mockLastName']}</div>
						</Form.Group>
					}
					{
						(state.alertType !== 'NAME') !== true && (state.criteriaFormatType !== 'COMBINED') !== true &&
						<Form.Group controlId='mockName'>
							<TextField formGroup={{ label: 'Name', fieldType: 'text', value: state.mockName, required: true, primaryCriteria: true }} onChange={handleChange} />
							<div style={{ color: "red", fontWeight: "bold" }}>{state.errors['mockName']}</div>
						</Form.Group>
					}
					{
						(state.alertType !== 'BUSINESS') !== true &&
						<Form.Group controlId='mockBusinessName'>
							<TextField formGroup={{ label: 'Business/Trust Name', fieldType: 'text', value: state.mockBusinessName, required: true, primaryCriteria: true }} onChange={handleChange} />
							<div style={{ color: "red", fontWeight: "bold" }}>{state.errors['mockBusinessName']}</div>
						</Form.Group>
					}
					{
						(state.alertType !== 'PARCEL_ID') !== true &&
						<Form.Group controlId='mockParcelId'>
							<TextField formGroup={{ label: 'Parcel ID', fieldType: 'text', value: state.mockParcelId, required: true, primaryCriteria: true }} onChange={handleChange} />
							<div style={{ color: "red", fontWeight: "bold" }}>{state.errors['mockParcelId']}</div>
						</Form.Group>
					}
				</Form.Row>
			</div>
	}


	let pageButtons = [];
	pageButtons.push({ label: 'Delete', onClickAction: showDeleteConfirmation, className: 'btn btn-danger btn-sm' });

	let formTemplateMarginLeft = 60
	let formTemplateMarginRight = 20
	if (sideNavigationOpen) {
		formTemplateMarginLeft = 270
	}

	let recipientsLabelStyle = { fontSize: '0.75em', marginLeft: 3 }
	if (state.errors['recipientsLabel'] === true) {
		recipientsLabelStyle = { fontSize: '1em', marginLeft: 3, color: "red", fontWeight: "bold" }
	}

	// ---------------- RENDER ----------------
	return (
		<div style={{ marginLeft: formTemplateMarginLeft, marginRight: formTemplateMarginRight, marginTop: 4, paddingBottom: 4, borderTop: '1px solid lightGray', boxShadow: "0 4px 8px 0 rgba(0, 0, 0, 0.55), 0 6px 20px 0 rgba(0, 0, 0, 0.54)" }}>
			<PageBanner pageHeading='Health Check' buttons={state.id ? pageButtons : null} />

			<Container fluid="true" style={{ paddingLeft: 10, paddingBottom: 20, marginTop: 10, marginLeft: 4, marginRight: 4 }}>
				<Row>
					<Col>
						<Form onSubmit={handleSubmit}>
							<Form.Row>
								<Form.Group controlId='name'>
									<TextField formGroup={{ id: "name", label: "Health Check Nickname", primaryCriteria: true, value: state.name, required: true }} onChange={handleChange} />
									<div style={{ fontSize: '0.75em', marginLeft: 3 }} >Give this health check a nickname to help you identify it.</div>
								</Form.Group>
							</Form.Row>
							{
								multiTenantSettings.multiTenant === true &&
								<Form.Row style={{ marginLeft: -1 }}>
									<Form.Group>
										{county}
										<div style={{ color: "red", fontWeight: "bold" }}>{state.errors['countyCode']}</div>
									</Form.Group>
								</Form.Row>
							}
							<Form.Row>
								<Form.Group>
									<SelectField
										formGroup={{ id: 'frequencyType', label: 'How often', fieldType: 'select', primaryCriteria: true, value: state.frequencyType, required: true, options: [{ code: "MONTHLY", description: "Monthly" }, { code: "WEEKLY", description: "Weekly" }, { code: "DAILY", description: "Daily" }, { code: "ONE_TIME", description: "One Time Only" }] }}
										onChange={handleChange}
									/>
								</Form.Group>
							</Form.Row>

							<Form.Row>
								<Form.Group controlId='dataType'>
									<SelectField formGroup={{ id: 'dataType', label: 'Use', fieldType: 'select', primaryCriteria: true, value: state.dataType, required: true, options: [{ code: "MOCK", description: "Mock Data" }, { code: "REAL", description: "Real Data" }] }}  disabled={disabledFlag}  onChange={handleChange} />
								</Form.Group>
							</Form.Row>

							{realDataSection}
							{mockDataSection}
							<h5 style={{ textDecoration: 'underline' }}>Health Check Recipients</h5>
							<div style={recipientsLabelStyle} >Enter at least one Email or Text(SMS) Recipient.</div>

							<h6>Up to {alertSettings.numberOfEmails} Email Recipients</h6>
							<Row>
								<Col lg={9} md={9} sm={12} xs={12}>
									{
										state.healthCheckEmailAddresses.map((emailAddress, index) => (
											<AlertEntryEmailRow id={index} key={emailAddress.id && emailAddress.id > 0 ? emailAddress.id : index} index={index} errors={state.errors} emailAddress={emailAddress} acknowledgement={alertSettings.emailAcknowledgement} onDelete={deleteEmail} onChange={handleChange} onClick={handleEmailLookup} onResend={resendEmailVerification} ></AlertEntryEmailRow>
										))
									}
								</Col>
							</Row>
							<Form.Row>
								{
									state.healthCheckEmailAddresses.length < alertSettings.numberOfEmails &&
									<Button variant="link" id='addEmail' className='shadow-none' onClick={addEmail}>{state.healthCheckEmailAddresses.length === 0 ? '+ Add an email' : '+ Add another email'}</Button>
								}
								{
									state.healthCheckEmailAddresses.length >= alertSettings.numberOfEmails &&
									<>&nbsp;</>
								}
							</Form.Row>

							<h6>Up to {alertSettings.numberOfPhones} Text(SMS) Recipients</h6>
							<Row>
								<Col lg={9} md={9} sm={12} xs={12}>
									{
										state.healthCheckPhones.map((phone, index) => (
											<AlertEntryPhoneRow id={index} key={phone.id && phone.id > 0 ? phone.id : index} index={index} errors={state.errors} phone={phone} acknowledgement={alertSettings.phoneAcknowledgement} onDelete={deletePhone} onChange={handleChange} onClick={handlePhoneLookup} onResend={resendPhoneVerification} ></AlertEntryPhoneRow>
										))
									}
								</Col>
							</Row>

							<Form.Row>
								{
									state.healthCheckPhones.length < alertSettings.numberOfPhones &&
									<Button variant="link" id='addPhone' className='shadow-none' onClick={addPhone}>{state.healthCheckPhones.length === 0 ? '+ Add a phone' : '+ Add another phone'}</Button>
								}
								{
									state.healthCheckPhones.length >= alertSettings.numberOfPhones &&
									<>&nbsp;</>
								}
							</Form.Row>
							<Row>&nbsp;</Row>
							<PrimaryButton buttonLabel="Done" />
						</Form>
					</Col>
				</Row>
			</Container>

			<Alert show={state.showAlert} message={state.alertMessage} hideAlert={() => updateState({ showAlert: false, alertMessage: '', alertComponentType: '' })} />
			<Loading loadingIndicator={state.loadingStatus === LOADING_STATUES.LOADING} />
			<Confirmation show={state.showDeleteConfirmationDialog} proceedConfirmation={handleDeleteAlert} cancelConfirmation={() => updateState({ showDeleteConfirmationDialog: false })} question={'Delete Health Check?'} />
			<EmailAddressSelectionList showModal={state.showEmailAddressList} onHide={(selectedId, selectedName) => handleCloseEmailLookup(selectedId, selectedName)} />
			<PhoneNumberSelectionList showModal={state.showPhoneNumberList} onHide={(selectedId, selectedName) => handleClosePhoneLookup(selectedId, selectedName)} />
		</div>
	);
}